import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { CheckCircle } from 'lucide-react'
import { PATHROUTES } from '@/constants/path'

interface PaymentConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  eventName?: string
  sponsorShipId?: string
}

function PaymentConfirmationDialog({
  open,
  onOpenChange,
  eventName = 'First Tracks Morning Session',
  sponsorShipId,
}: Readonly<PaymentConfirmationDialogProps>) {
  const navigate = useNavigate()

  const handleCancel = () => {
    onOpenChange(false)
  }

  const handleAdUpload = () => {
    onOpenChange(false)

    if (sponsorShipId) {
      // Navigate to specific event ad upload page if we have an eventId
      navigate(PATHROUTES.DASHBOARD.UPLOAD_AD_WITH_ID(sponsorShipId))
    } else {
      // Navigate to general sponsorship management
      navigate(PATHROUTES.DASHBOARD.SPONSORSHIP_MANAGEMENT)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[468px] w-[90vw]"
        showCloseButton={false}
      >
        <div className="flex flex-col gap-4">
          {/* Featured Icon */}
          <div className="w-10 h-10 bg-ss-blue-50 rounded-full flex items-center justify-center shrink-0 self-center sm:self-start">
            <CheckCircle
              className="w-5 h-5 text-ss-blue-500"
              aria-hidden="true"
            />
          </div>

          {/* Text Content */}
          <div className="flex flex-col gap-3 w-full">
            <h2 className="text-lg font-semibold text-ss-neutral-950 leading-7 text-center sm:text-left">
              Your sponsorship is confirmed!
            </h2>

            <div className="flex flex-col gap-3">
              <p className="text-sm font-normal text-ss-neutral-950 opacity-88 leading-[22px] text-center sm:text-left">
                Your sponsorship for "{eventName}" is confirmed. You can now
                upload your ads
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="h-9 px-4 border-ss-neutral-200 text-ss-neutral-950 text-sm font-semibold hover:bg-ss-neutral-25 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAdUpload}
              className="h-9 px-4 bg-ss-blue-900 text-white text-sm font-semibold hover:bg-ss-blue-800 rounded-lg"
            >
              Ad Upload
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PaymentConfirmationDialog
